import { StarIcon as StarIconOutline } from "@heroicons/react/24/outline";
import { ChevronLeftIcon, ChevronRightIcon, StarIcon } from "@heroicons/react/24/solid";
import { useCallback, useEffect, useState } from "react";
import { parseJsonField } from "~/database/d1-client";
import type { ProductReviewRow, ProductReviewsResponse, RatingStats } from "~/types";
import { formatDistanceToNow } from "~/utils/dates";
import Neum from "../neum.component";

export default function ProductReviews({
  productHandle,
}: {
  productHandle: string;
}) {
  const [reviews, setReviews] = useState<ProductReviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ratingStats, setRatingStats] = useState<RatingStats>({
    average: 0,
    total: 0,
    distribution: { "1": 0, "2": 0, "3": 0, "4": 0, "5": 0 },
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalReviews, setTotalReviews] = useState(0);
  const reviewsPerPage = 5;

  const fetchReviews = useCallback(
    async (page: number) => {
      setLoading(true);
      try {
        const skip = (page - 1) * reviewsPerPage;
        const response = await fetch(
          `/api/product-reviews?productHandle=${productHandle}&limit=${reviewsPerPage}&skip=${skip}`,
        );

        if (!response.ok) {
          throw new Error("Failed to fetch reviews");
        }

        const data: ProductReviewsResponse = await response.json();
        setReviews(data.reviews);
        setRatingStats(data.ratingStats);
        setTotalReviews(data.total);
      } catch (err) {
        console.error("productReviews - Error fetching product reviews:", err);
        setError("Unable to load reviews at this time");
      } finally {
        setLoading(false);
      }
    },
    [productHandle],
  );

  useEffect(() => {
    fetchReviews(currentPage);
  }, [fetchReviews, currentPage]);

  const totalPages = Math.ceil(totalReviews / reviewsPerPage);

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
      // Scroll to top of reviews section
      document.getElementById("product-reviews")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Render star rating (filled and empty stars)
  const renderStars = (rating: number) => {
    return (
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <span key={star}>
            {star <= rating ? (
              <StarIcon className="h-5 w-5 text-yellow-400" />
            ) : (
              <StarIconOutline className="h-5 w-5 text-yellow-400" />
            )}
          </span>
        ))}
      </div>
    );
  };

  // Calculate percentage for rating bar
  const calculatePercentage = (count: number) => {
    if (ratingStats.total === 0) return 0;
    return (count / ratingStats.total) * 100;
  };

  if (ratingStats.total === 0) {
    return (
      <div id="product-reviews" className="mt-10 px-1">
        <h2 className="text-2xl font-bold text-admirerBlack-900 dark:text-white">Customer Reviews</h2>
        <p className="text-lg text-admirerBlack-600 dark:text-admirerGray-200">
          You could be the first to leave a review for this product!
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div id="product-reviews" className="mt-10 px-1">
        <h2 className="text-2xl font-bold text-admirerBlack-900 dark:text-white">Customer Reviews</h2>
        <Neum className="mt-4 p-8 text-center">
          <p className="text-red-500">{error}</p>
        </Neum>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6 sm:py-12 lg:grid lg:max-w-7xl lg:grid-cols-12 lg:min-h-screen ">
      <div id="product-reviews" className="lg:col-span-4 sm:sticky top-0 h-fit max-h-screen">
        <h2 className="text-2xl font-bold text-admirerBlack-900 dark:text-white">Customer Reviews</h2>
        {/* Rating summary */}
        <div className="lg:col-span-8 mt-3 flex items-center">
          <div>
            <div className="flex items-center">
              {/* Average rating */}

              <div className="mt-2">{renderStars(ratingStats.average)}</div>
              <div className="mt-2 ml-2 text-sm text-admirerBlack-600 dark:text-admirerGray-200">
                Based on {ratingStats.total} {ratingStats.total === 1 ? "review" : "reviews"}
              </div>
            </div>
            <div className="mt-6">
              {/* Rating distribution */}
              <div className="col-span-2">
                {[5, 4, 3, 2, 1].map((rating) => (
                  <div key={rating} className="mb-2 grid grid-cols-12 items-center gap-2">
                    <div className="col-span-1 text-sm font-medium text-admirerBlack-900 dark:text-white">{rating}</div>
                    <div className="col-span-1">
                      <StarIcon className="h-4 w-4 text-yellow-400" />
                    </div>
                    <div className="col-span-8">
                      <div className="h-2.5 w-full rounded-full border-gray-200 bg-gray-300 dark:bg-admirerBlack-300">
                        <div
                          className="h-2.5 rounded-full bg-admirerBlue"
                          style={{
                            width: `${calculatePercentage(ratingStats.distribution[rating] || 0)}%`,
                          }}
                        />
                      </div>
                    </div>
                    <div className="col-span-2 text-right text-sm font-medium text-admirerBlack-600 dark:text-admirerGray-200">
                      {ratingStats.distribution[rating] || 0}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* <div className="mt-10">
              <h3 className="text-lg font-medium text-gray-900">
                Share your thoughts
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                If you’ve used this product, share your thoughts with other
                customers
              </p>

              <a
                href="##"
                className="mt-6 inline-flex w-full items-center justify-center rounded-md border border-gray-300 bg-white px-8 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 sm:w-auto lg:w-full"
              >
                Write a review
              </a>
            </div> */}
          </div>
        </div>
      </div>
      {/* Reviews list */}
      <div className="mt-16 lg:col-span-7 lg:col-start-6 lg:mt-0">
        <h3 className="sr-only">Recent reviews</h3>
        <div className="flow-root">
          <div className="-my-12 divide-y divide-gray-300">
            {loading ? (
              <Neum className="p-8 text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-admirerBlue border-r-transparent align-middle" />
                <p className="mt-4 text-admirerBlack-600 dark:text-admirerGray-200">Loading reviews...</p>
              </Neum>
            ) : (
              <>
                {reviews.map((review) => (
                  <div key={`${review.product_handle}-${review.customer_id}`} className="px-0 py-4 md:p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-admirerBlue text-lg font-bold text-white">
                          {review.customer_first_name.charAt(0)}
                          {review.customer_last_name.charAt(0)}
                        </div>
                        <div className="ml-4">
                          <h3 className="text-lg font-bold text-admirerBlack-900 dark:text-white">
                            {review.customer_first_name} {review.customer_last_name.charAt(0)}.
                          </h3>
                          <div className="flex items-center">
                            {renderStars(review.rating)}
                            {Boolean(review.verified) && (
                              <span className="ml-2 rounded-full bg-admirerBlue/10 px-2 py-0.5 text-xs font-medium text-admirerBlue">
                                Verified Purchase
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* <div className="text-sm text-admirerBlack-500 dark:text-admirerGray-300">
                        {formatDistanceToNow(new Date(review.created_at))}
                      </div> */}
                    </div>

                    <h4 className="mt-4 text-lg font-medium text-admirerBlack-900 dark:text-white">{review.title}</h4>
                    <p className="mt-2 whitespace-pre-line text-admirerBlack-700 dark:text-admirerGray-200">
                      {review.body}
                    </p>

                    {/* Review images if available */}
                    {/* {review.images && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {parseJsonField<string[]>(review.images, []).map(
                          (image, index) => (
                            <img
                              key={`${review.customer_id}-image-${index}`}
                              src={image}
                              alt={`Product review by ${
                                review.customer_first_name
                              } ${review.customer_last_name.charAt(0)}.`}
                              className="h-20 w-20 rounded-md object-cover"
                            />
                          )
                        )}
                      </div>
                    )} */}

                    {/* Helpful button */}
                    {/* <div className="mt-4 flex items-center">
                      <button
                        type="button"
                        className="flex items-center rounded-md bg-admirerGray-100 px-3 py-1.5 text-sm font-medium text-admirerBlack-700 transition hover:bg-admirerGray-200 dark:bg-admirerBlack-400 dark:text-admirerGray-200 dark:hover:bg-admirerBlack-300"
                      >
                        <svg
                          role="img"
                          aria-label="Helpful icon"
                          xmlns="http://www.w3.org/2000/svg"
                          className="mr-1.5 h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
                          />
                        </svg>
                        Helpful {review.helpful ? `(${review.helpful})` : ""}
                      </button>
                    </div> */}
                  </div>
                ))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="pt-8 flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`mr-2 flex h-10 w-10 items-center justify-center rounded-full ${
                        currentPage === 1
                          ? "cursor-not-allowed bg-admirerGray-200 text-admirerGray-500 dark:bg-admirerBlack-400 dark:text-admirerGray-400"
                          : "bg-admirerGray-100 text-admirerBlack-700 hover:bg-admirerGray-200 dark:bg-admirerBlack-400 dark:text-admirerGray-200 dark:hover:bg-admirerBlack-300"
                      }`}
                      aria-label="Previous page"
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                    </button>

                    <div className="flex items-center space-x-2">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }

                        return (
                          <button
                            type="button"
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`flex h-10 w-10 items-center justify-center rounded-full ${
                              currentPage === pageNum
                                ? "bg-admirerBlue text-white"
                                : "bg-admirerGray-100 text-admirerBlack-700 hover:bg-admirerGray-200 dark:bg-admirerBlack-400 dark:text-admirerGray-200 dark:hover:bg-admirerBlack-300"
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      type="button"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`ml-2 flex h-10 w-10 items-center justify-center rounded-full ${
                        currentPage === totalPages
                          ? "cursor-not-allowed bg-admirerGray-200 text-admirerGray-500 dark:bg-admirerBlack-400 dark:text-admirerGray-400"
                          : "bg-admirerGray-100 text-admirerBlack-700 hover:bg-admirerGray-200 dark:bg-admirerBlack-400 dark:text-admirerGray-200 dark:hover:bg-admirerBlack-300"
                      }`}
                      aria-label="Next page"
                    >
                      <ChevronRightIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
